import { useRouter } from 'next/router';
import { docBySlugQuery, guidesIndexQueryProduction } from '@lib/queries';
import { getClient } from '@lib/sanity.server';
import { Box } from '@chakra-ui/react';
import { SkipNavLink, SkipNavContent } from '@chakra-ui/skip-nav';
import {
  DocsSidenav,
  DocsHeader,
  DocsTemplate,
  DocsFooter,
  DocsHome,
  DocsIntegrations,
  DocsQuickstarts,
  DocsAPIReference,
  DocsComponentReference
} from '@components';
import {
  compileDocsContent,
  fetchDocsContentForSlug,
  fetchDocsLastUpdatedForSlug,
  fetchDocsNavigationData,
  fetchDocsStaticPaths
} from '@lib/docs/server';
import { isProduction } from '@utils';
import React from 'react';

const OVERVIEW_PAGES = {
  'quickstarts/overview': DocsQuickstarts,
  'integrations/overview': DocsIntegrations,
  'references/overview': DocsAPIReference,
  'components/overview': DocsComponentReference
};

export const FrontmatterContext = React.createContext<{
  frontmatter: Record<string, unknown>;
  title: string;
}>({} as any);

export default function DocsPage({
  sanityData,
  guidesData,
  mdx,
  mdxNav,
  slug
}: any) {
  const router = useRouter();

  // if (!mdx || !router.isFallback) {
  //   return <ErrorPage statusCode={404} />;
  // }

  let content = <DocsTemplate content={sanityData} mdx={mdx} />;

  if (slug === 'index') {
    content = <DocsHome />;
  }

  // @ts-expect-error -- We don't know if there is a matching overview page for slug yet
  const OverviewComponent = OVERVIEW_PAGES[slug];

  if (OverviewComponent) {
    content = (
      <OverviewComponent
        guides={guidesData}
        content={{ title: mdx?.frontmatter?.title }}
      />
    );
  }

  return router.isFallback ? (
    <div>Loading...</div>
  ) : (
    <FrontmatterContext.Provider value={mdx.frontmatter}>
      <Box minH="100vh" bg="var(--bodyBg)" color="var(--primaryTextColor)">
        <SkipNavLink zIndex={20}>Skip to Content</SkipNavLink>
        <DocsSidenav mdxNav={mdxNav} />
        <DocsHeader mdxNav={mdxNav} />
        <SkipNavContent />
        {content}
        <DocsFooter />
      </Box>
    </FrontmatterContext.Provider>
  );
}

export async function getStaticProps({ params, preview = false }: any) {
  const slug = params?.slug?.join?.('/') ?? 'index';

  try {
    const [content, lastUpdated, mdxNav] = await Promise.all([
      fetchDocsContentForSlug(slug),
      // Only runs on production to avoid hitting the GitHub API rate limit with preview deployments
      isProduction ? fetchDocsLastUpdatedForSlug(slug) : null,
      fetchDocsNavigationData()
    ]);

    const mdx = await compileDocsContent(content, {
      slug,
      lastUpdated
    });

    const sanitySlug: string = mdx?.frontmatter?.sanity_slug as string;

    let seoData = {
      title: mdx?.frontmatter?.title ?? null,
      description: mdx?.frontmatter?.description ?? null,
      ogImage: `https://clerk.com/api/og?title=${encodeURIComponent(
        mdx.frontmatter.title as string
      )}&description=${encodeURIComponent(
        mdx.frontmatter.description as string
      )}`
    };

    let sanityData = null;
    let guidesData = null;

    // TODO(bryce): This is a temporary flow to handle rendering overview pages from Sanity content.
    // These pages have slightly different data requirements that we need to account for.
    // @ts-expect-error -- We don't know if there is a matching overview page for slug yet
    if (OVERVIEW_PAGES[slug]) {
      guidesData = await getClient(preview).fetch(guidesIndexQueryProduction);
    }

    if (sanitySlug) {
      try {
        sanityData = await getClient(preview).fetch(docBySlugQuery, {
          // Slug query requires full path including category
          slug: sanitySlug
        });

        // If we don't get any data back from sanity, assume the content doesn't exist and return notFound
        if (sanityData === null) {
          console.warn('[docs] sanity content not found for slug:', sanitySlug);

          return {
            notFound: true
          };
        }

        seoData = sanityData.seo;
      } catch (error) {
        console.error(
          '[docs] error fetching sanity content for slug',
          sanitySlug,
          error
        );

        return {
          notFound: true
        };
      }
    }

    // Set SEO prop from data
    return {
      props: {
        seo: seoData,
        sanityData,
        guidesData,
        mdx: sanityData ? null : mdx,
        mdxNav,
        preview,
        slug
      }
    };
  } catch (error: any) {
    console.error('[docs] error generating props:', error);

    if (error.name === 'DocumentNotFoundError') {
      return {
        notFound: true
      };
    }

    // If it's *not* a not found error, re-throw to ensure we aren't improperly caching 404s
    throw error;
  }
}

export async function getStaticPaths() {
  const paths = await fetchDocsStaticPaths();

  return {
    paths: paths.map((slug: string) => ({ params: { slug: slug.split('/') } })),
    fallback: 'blocking'
  };
}
